import React from "react";
import { View, StyleSheet, Pressable } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import ThemedInput from "@/components/ThemedInput";
import { Button } from "@/components/ui/Button";
import { ToggleSwitch } from "@/components/ui/ToggleSwitch";
import { TreeNode } from "@/components/ui/TreeNode";
import { useTheme } from "@/hooks/useTheme";
import type { SectionType, QuestionType } from "@/types/db/courses";
import { QuestionCard } from "./QuestionCard";

export interface SectionCardProps {
  section: SectionType;
  sectionIndex: number;
  isLast: boolean;
  onUpdate: (updates: Partial<SectionType>) => void;
  onDelete: () => void;
  onAddQuestion: () => void;
  onUpdateQuestion: (questionIndex: number, updates: Partial<QuestionType>) => void;
  onDeleteQuestion: (questionIndex: number) => void;
  onChangeQuestionType: (questionIndex: number, newType: QuestionType["type"]) => void;
}

export function SectionCard({
  section,
  sectionIndex,
  isLast,
  onUpdate,
  onDelete,
  onAddQuestion,
  onUpdateQuestion,
  onDeleteQuestion,
  onChangeQuestionType,
}: SectionCardProps) {
  const { colors } = useTheme();

  return (
    <TreeNode depth={0} isLast={isLast}>
      <View style={[styles.card, { backgroundColor: colors.card, borderColor: colors.border }]}>
        {/* Header */}
        <View style={styles.header}>
          <ThemedText type="subtitle">Section {sectionIndex + 1}</ThemedText>
          <Button
            icon="trash-can-outline"
            onPress={onDelete}
            iconColor="#ef4444"
            testID={`delete-section-${sectionIndex}`}
            style={{ backgroundColor: "transparent" }}
          />
        </View>

        {/* Title */}
        <ThemedInput
          label="Section Title"
          value={section.title}
          onChangeText={(title) => onUpdate({ title })}
          placeholder="e.g., Introduction to Safety Procedures"
          testID={`section-${sectionIndex}-title`}
        />

        {/* Description */}
        <ThemedInput
          label="Description"
          value={section.description}
          onChangeText={(description) => onUpdate({ description })}
          placeholder="Describe what this section covers..."
          multiline
          numberOfLines={3}
          inputStyle={{ minHeight: 80, textAlignVertical: "top" }}
          testID={`section-${sectionIndex}-description`}
        />

        {/* Required to Pass */}
        <View style={styles.toggleRow}>
          <View style={{ flex: 1 }}>
            <ThemedText style={styles.label}>Required to Pass</ThemedText>
            <ThemedText style={styles.helperText}>Students must pass this section to complete the course</ThemedText>
          </View>
          <ToggleSwitch
            value={section.requiredToPass}
            onValueChange={(val) => onUpdate({ requiredToPass: val })}
            testID={`section-${sectionIndex}-required`}
          />
        </View>

        {/* Section Settings Override (optional) */}
        {section.settings && Object.keys(section.settings).length > 0 && (
          <View style={styles.settingsHint}>
            <ThemedText style={styles.helperText}>
              This section has custom settings that override course defaults
            </ThemedText>
          </View>
        )}
      </View>

      {/* Questions */}
      {section.questions.map((question, questionIndex) => (
        <QuestionCard
          key={question.id || questionIndex}
          question={question}
          questionIndex={questionIndex}
          isLast={questionIndex === section.questions.length - 1}
          onUpdate={(updates) => onUpdateQuestion(questionIndex, updates)}
          onDelete={() => onDeleteQuestion(questionIndex)}
          onChangeType={(newType) => onChangeQuestionType(questionIndex, newType)}
        />
      ))}

      {/* Add Question Button */}
      <TreeNode depth={1} isLast={true} isAddButton>
        <Pressable onPress={onAddQuestion} style={styles.addButton} testID={`add-question-section-${sectionIndex}`}>
          <ThemedText style={styles.addButtonText}>+ Add Question</ThemedText>
        </Pressable>
      </TreeNode>
    </TreeNode>
  );
}

const styles = StyleSheet.create({
  card: {
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    gap: 16,
    marginBottom: 8,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  label: {
    fontSize: 14,
    fontWeight: "600",
    marginBottom: 4,
  },
  helperText: {
    fontSize: 12,
    opacity: 0.7,
  },
  toggleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    paddingVertical: 8,
  },
  settingsHint: {
    padding: 12,
    borderRadius: 8,
    opacity: 0.8,
  },
  addButton: {
    padding: 12,
    alignItems: "center",
    justifyContent: "center",
  },
  addButtonText: {
    fontSize: 14,
    opacity: 0.7,
  },
});
